// @ts-check

/** @file Shared duration text input with inline format validation. */

import Text from "@kaspernj/api-maker/build/utils/text"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {memo, useEffect} from "react"
import {View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"

import TextInput from "./text-input"
import {parseDurationSeconds} from "../shared/duration"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} DurationInputProps
 * @property {string} [defaultValue] Initial uncontrolled value.
 * @property {boolean} [disabled] Whether text entry and keyboard submit should be disabled.
 * @property {string} [errorText] External validation error to show below the input.
 * @property {string} label Field label.
 * @property {(value: string) => unknown} [onChangeText] Callback for text changes.
 * @property {(event: {nativeEvent?: {text?: string}}) => unknown} [onEndEditing] Callback for committed text changes.
 * @property {(event: {nativeEvent?: {text?: string}}) => unknown} [onSubmitEditing] Callback for keyboard submit.
 * @property {string} [placeholder] Placeholder text.
 * @property {string} testID Stable test id for the input itself.
 * @property {string} [value] Controlled value.
 */

/**
 * @typedef {object} DurationInputState
 * @property {string} draftValue Current uncontrolled value.
 * @property {boolean} touched Whether the user has changed or committed the input.
 */

/**
 * Duration input that accepts decimal hours and unit-suffixed text.
 * @augments {ShapeComponent<DurationInputProps, DurationInputState>}
 */
class DurationInput extends ShapeComponent {
  static propTypes = PropTypesExact({
    defaultValue: PropTypes.string,
    disabled: PropTypes.bool,
    errorText: PropTypes.string,
    label: PropTypes.string.isRequired,
    onChangeText: PropTypes.func,
    onEndEditing: PropTypes.func,
    onSubmitEditing: PropTypes.func,
    placeholder: PropTypes.string,
    testID: PropTypes.string.isRequired,
    value: PropTypes.string
  })
  static defaultProps = {
    defaultValue: undefined,
    disabled: false,
    errorText: undefined,
    onChangeText: undefined,
    onEndEditing: undefined,
    onSubmitEditing: undefined,
    placeholder: undefined,
    value: undefined
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {DurationInputState} */
  state = {
    draftValue: "",
    touched: false
  }

  /** Wires locale and theme state. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const defaultValue = this.p.defaultValue
    const isControlled = this.p.value !== undefined

    useEffect(() => {
      if (isControlled) return
      this.s.draftValue = defaultValue || ""
      this.s.touched = false
    }, [defaultValue, isControlled])
  }

  /**
   * Renders the duration field.
   * @returns {ReactNode} Duration input field.
   */
  render() {
    const {defaultValue, disabled, label, onSubmitEditing, placeholder, testID, value} = this.p
    const currentValue = value !== undefined ? value : this.s.draftValue
    const errorText = this.errorText(currentValue)
    const themeName = this.tt.themeName
    const colors = this.tt.colors

    return (
      <View
        style={styles.field ||= {
          gap: 6
        }}
        testID={`${testID}/field`}
      >
        <Text
          style={styles[`label-${themeName}`] ||= {
            color: colors.textMuted,
            fontWeight: 600
          }}
          testID={`${testID}/label`}
        >
          {label}
        </Text>
        <TextInput
          defaultValue={value === undefined ? defaultValue : undefined}
          editable={!disabled}
          onChangeText={this.tt.onChangeText}
          onEndEditing={this.tt.onEndEditing}
          onSubmitEditing={!disabled && onSubmitEditing ? this.tt.onSubmitEditing : undefined}
          placeholder={placeholder || _("1h 5m, 30s or 1.5")}
          style={styles.input ||= {
            fontSize: 16
          }}
          testID={testID}
          value={value}
        />
        {Boolean(errorText) &&
          <Text
            style={styles[`errorText-${themeName}`] ||= {
              color: colors.dangerText,
              fontSize: 13,
              fontWeight: 600
            }}
            testID={`${testID}/errorText`}
          >
            {errorText}
          </Text>
        }
      </View>
    )
  }

  /**
   * Computes visible validation copy for the current input.
   * @param {string} value Current duration text.
   * @returns {string} Error text, or blank when valid.
   */
  errorText(value) {
    if (this.p.errorText) return this.p.errorText
    if (!this.s.touched || !value.trim()) return ""
    if (parseDurationSeconds(value) != null) return ""
    return _("Enter a duration like 1h 5m, 30s or 1.5")
  }

  /**
   * Stores changed text and forwards it to the caller.
   * @param {string} value New duration text.
   * @returns {void} Nothing.
   */
  onChangeText = (value) => {
    if (this.p.value === undefined) this.s.draftValue = value
    this.s.touched = true
    this.p.onChangeText?.(value)
  }

  /**
   * Stores committed native text and forwards the event to the caller.
   * @param {{nativeEvent?: {text?: string}}} event Native text event.
   * @returns {void} Nothing.
   */
  onEndEditing = (event) => {
    const value = event.nativeEvent?.text || ""

    if (this.p.value === undefined) this.s.draftValue = value
    this.s.touched = true
    this.p.onEndEditing?.(event)
  }

  /**
   * Stores submitted native text and forwards the event to the caller.
   * @param {{nativeEvent?: {text?: string}}} event Native text event.
   * @returns {void} Nothing.
   */
  onSubmitEditing = (event) => {
    const value = event.nativeEvent?.text || ""

    if (this.p.value === undefined) this.s.draftValue = value
    this.s.touched = true
    this.p.onSubmitEditing?.(event)
  }
}

/** @type {NamedExoticComponent<DurationInputProps>} */
const DurationInputComponent = memo(shapeComponent(DurationInput))

export default DurationInputComponent
